// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HomePageContext } from "@insite/content-library/Pages/HomePage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Carousel, { CarouselSlide } from "@mcmc/Overrides/Components/Carousel";
import StyledBox from "@mcmc/XDL/Components/Common/Box";
import CloudinaryImage from "@mcmc/XDL/Components/Common/CloudinaryImage";
import MultilineEllipsis from "@mcmc/XDL/Components/Common/MultiLineEllipsis";
import { extendDataLayerWithEnvent, Roboto } from "@mcmc/XDL/Utilities";
import { transformationsMap } from "@mcmc/XDL/Utilities/cloudinary";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: WidgetProps) => {
    const session = getSession(state);
    const billToState = getBillToState(state, session.billToId);
    const { value: billTo } = billToState;
    return {
        userName: `${session.firstName || ""} ${session.lastName || ""}`,
        companyName: billTo?.companyName,
        myAccountLink: getPageLinkByPageType(state, "MyAccountPage"),
        invoicesLink: getPageLinkByPageType(state, "InvoiceHistoryPage"),
        orderHistoryLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        products: getProductsForProductInfoList(state, ownProps.id),
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface HomePageAccountTilesStyles {
    wrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    userName?: TypographyPresentationProps;
    companyName?: TypographyPresentationProps;
    linksWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    carouselContainer?: InjectableCss;
    carouselCard?: LinkPresentationProps;
    carouselImage?: LazyImageProps;
}

const homePageAccountTilesStyles: HomePageAccountTilesStyles = {
    wrapper: {
        css: css`
            margin: 40px 0;
            display: flex;
            flex-wrap: wrap;
            font-size: 16px;
            line-height: 19px;
            padding: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-direction: column;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    infoWrapper: {
        css: css`
            padding: 20px;
            border-right: 1px solid ${getColor("common.ui3")};
            border-bottom: 1px solid ${getColor("common.ui3")};
            min-width: 180px;
        `,
    },
    userName: {
        variant: "h1",
        forwardAs: "h2",
        css: css`
            margin-top: 10px;
            margin-bottom: 0;
        `,
    },
    linksWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            border-bottom: 1px solid ${getColor("common.ui3")};
            padding-left: 45px;
            flex: 1;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-wrap: wrap;
                            padding: 15px 20px;
                        `,
                        css`
                            padding-left: 20px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    link: {
        typographyProps: { transform: "uppercase" },
        css: css`
            ${getProp("theme.link.defaultProps.css")}
            margin-right: 40px;
            margin-bottom: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-top: 5px;
                            margin-bottom: 5px;
                        `,
                        css`
                            margin-right: 20px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    carouselContainer: {
        css: css`
            padding: 15px 20px 25px 20px;
            width: 100%;
        `,
    },
    carouselCard: {
        color: "text",
        hoverStyle: { color: "text" },
        typographyProps: { ...Roboto, size: 14, lineHeight: "16px" },
        css: css`
            border: 1px solid ${getColor("common.ui3")};
            width: 192px;
            height: 100%;
            align-items: flex-end;
            transition: border-color 0.2s;
            &:focus,
            &:hover {
                outline: none;
                border-color: ${getColor("text")};
            }

            & > ${TypographyStyle} {
                width: 100%;
            }

            .title-text {
                display: block;
                padding: 15px 10px 20px 10px;
                text-align: center;
                height: 67px;
            }
        `,
    },
    carouselImage: {
        width: "100%",
        height: "140px",
    },
};

const styles = homePageAccountTilesStyles;

const HomePageAccountTiles = ({
    id,
    userName,
    companyName,
    myAccountLink,
    invoicesLink,
    orderHistoryLink,
    products,
    loadCarouselProducts,
}: Props) => {
    useEffect(() => {
        if (!products) {
            loadCarouselProducts({
                carouselType: "recentlyViewed",
                carouselId: id,
                isBrandDetailsPage: false,
                isProductListPage: false,
                isProductDetailsPage: false,
                relatedProductType: "",
                numberOfProductsToDisplay: 5,
                seedWithManuallyAssigned: "",
                selectedCategoryIds: [],
                displayProductsFrom: "allCategories",
            });
        }
    }, []);
    const [slides, setSlides] = useState<CarouselSlide[]>([]);
    useEffect(() => {
        if (products && products.length > 0) {
            setSlides(mapProductsToSlides(products));
        }
    }, [products]);

    return (
        <StyledBox {...styles.wrapper}>
            <StyledWrapper {...styles.infoWrapper}>
                <Typography {...styles.userName}>{userName}</Typography>
                {companyName && <Typography {...styles.companyName}>{companyName}</Typography>}
            </StyledWrapper>
            <StyledWrapper {...styles.linksWrapper}>
                <Link {...styles.link} href={myAccountLink?.url}>
                    {translate("My Account")}
                </Link>
                {!invoicesLink?.excludeFromNavigation && (
                    <Link {...styles.link} href={invoicesLink?.url}>
                        {translate("Invoices")}
                    </Link>
                )}
                <Link {...styles.link} href={orderHistoryLink?.url}>
                    {translate("Order History")}
                </Link>
                <Link
                    {...styles.link}
                    href={`${orderHistoryLink?.url}?customerSequance=-1&orderType=open`}
                    aria-label={translate("Open Orders")}
                >
                    {translate("Open Orders")}
                </Link>
            </StyledWrapper>
            {slides.length > 0 && (
                <StyledWrapper {...styles.carouselContainer}>
                    <Carousel
                        id={id}
                        maxNumberOfColumns={5}
                        slideGap={27}
                        title={translate("My Recently Viewed Items")}
                        slides={slides}
                        extendedStyles={{ titleText: { variant: "h6" } }}
                    />
                </StyledWrapper>
            )}
        </StyledBox>
    );
};

// <XNG-Changes>
const handleClickRecentlyViewed = () => {
    extendDataLayerWithEnvent("recently-viewd-items");
};
// <XNG-Changes>

const mapProductsToSlides = (products: ProductModel[]) =>
    products.map(product => ({
        id: product.id,
        renderComponent: function ProductCarouselProductCondensedRenderer() {
            let productTitle = `${product.productTitle}`;
            if (product.brand && product.brand.name) {
                productTitle = `${product.brand.name} ${product.productTitle}`;
            }
            return (
                <Link href={product.canonicalUrl} {...styles.carouselCard} onClick={handleClickRecentlyViewed}>
                    <CloudinaryImage
                        {...styles.carouselImage}
                        imageTransformations={transformationsMap.product_sm}
                        src={product.smallImagePath || product.mediumImagePath}
                        altText={product.productTitle}
                    />
                    <Typography className="title-text">
                        <MultilineEllipsis
                            content={productTitle}
                            maxLines={2}
                            forwardAs="span"
                            {...styles.carouselCard?.typographyProps}
                        />
                    </Typography>
                </Link>
            );
        },
    }));

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(HomePageAccountTiles),
    definition: {
        group: "Basic",
        icon: "PageTitle",
        allowedContexts: [HomePageContext],
        displayName: "Home Page Account Tiles",
    },
};

export default widgetModule;
